import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import SEO from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import {
  Settings, Workflow, Zap, BarChart3, FileText, Users,
  CreditCard, Truck, Shield, HeadphonesIcon, Database,
  Calendar, Receipt, UserCheck, Building, Clipboard,
  Package, AlertTriangle, CheckCircle, Play
} from "lucide-react";
import RelatedLinks from "@/components/ui/related-links";
import { AuroraHero } from "@/components/ui/aurora-hero";

const Flows = () => {
  const categories = [
    {
      name: "Financeiro",
      color: "fs-purple",
      description: "Gestão financeira completa"
    },
    {
      name: "Recursos Humanos",
      color: "fs-cyan", 
      description: "Processos de RH automatizados"
    },
    {
      name: "Qualidade",
      color: "fs-magenta",
      description: "Controle de qualidade total"
    },
    {
      name: "TI Services",
      color: "fs-purple",
      description: "Serviços de tecnologia"
    }
  ];

  const fluigApps = [
    // Categoria: Financeiro
    {
      id: 1,
      name: "Gestão de Contratos",
      category: "Financeiro",
      icon: <FileText className="w-6 h-6" />,
      description: "Sistema completo para gestão do ciclo de vida de contratos, desde criação até renovação",
      features: ["Controle de vigência", "Alertas automáticos", "Gestão de aditivos", "Relatórios gerenciais"],
      benefits: "Reduz 80% do tempo gasto em gestão contratual"
    },
    {
      id: 2,
      name: "Controle de Despesas",
      category: "Financeiro", 
      icon: <CreditCard className="w-6 h-6" />,
      description: "Automatização completa do processo de prestação de contas e reembolsos",
      features: ["Captura de notas", "Validação automática", "Aprovação hierárquica", "Integração contábil"],
      benefits: "Acelera 70% o processo de reembolso"
    },
    {
      id: 3,
      name: "Contas a Pagar",
      category: "Financeiro",
      icon: <Receipt className="w-6 h-6" />,
      description: "Workflow completo para gestão de contas a pagar com aprovações automáticas",
      features: ["Três vias", "Aprovação por alçada", "Integração ERP", "Dashboard financeiro"],
      benefits: "Elimina 90% dos erros manuais"
    },
    {
      id: 4,
      name: "Orçamento Empresarial",
      category: "Financeiro",
      icon: <BarChart3 className="w-6 h-6" />,
      description: "Planejamento e controle orçamentário com análise de desvios",
      features: ["Planejamento anual", "Controle mensal", "Análise de desvios", "Relatórios executivos"],
      benefits: "Melhora 60% a precisão orçamentária"
    },

    // Categoria: Recursos Humanos  
    {
      id: 5,
      name: "Admissão de Funcionários",
      category: "Recursos Humanos",
      icon: <UserCheck className="w-6 h-6" />,
      description: "Processo digitalizado de admissão com checklist automático",
      features: ["Checklist digital", "Documentos online", "Integração eSocial", "Onboarding"],
      benefits: "Acelera 50% o processo admissional"
    },
    {
      id: 6,
      name: "Avaliação de Desempenho",
      category: "Recursos Humanos",
      icon: <Users className="w-6 h-6" />,
      description: "Sistema completo de avaliação 360º com metas e indicadores",
      features: ["Avaliação 360º", "Metas SMART", "Feedback contínuo", "PDI automático"],
      benefits: "Aumenta 40% o engajamento dos colaboradores"
    },
    {
      id: 7,
      name: "Solicitações de RH",
      category: "Recursos Humanos", 
      icon: <Clipboard className="w-6 h-6" />,
      description: "Portal único para todas as solicitações de recursos humanos",
      features: ["Portal self-service", "Múltiplos tipos", "Aprovação automática", "Histórico completo"],
      benefits: "Reduz 65% as consultas ao RH"
    },
    {
      id: 8,
      name: "Gestão de Férias",
      category: "Recursos Humanos",
      icon: <Calendar className="w-6 h-6" />,
      description: "Controle automatizado de férias e ausências com agenda visual",
      features: ["Calendário visual", "Conflitos automáticos", "Aprovação hierárquica", "Relatórios legais"],
      benefits: "Elimina 100% dos conflitos de agenda"
    },

    // Categoria: Qualidade
    {
      id: 9,
      name: "Não Conformidades",
      category: "Qualidade",
      icon: <AlertTriangle className="w-6 h-6" />,
      description: "Gestão completa de não conformidades com planos de ação",
      features: ["Registro digital", "Plano de ação", "Análise de causa", "Indicadores qualidade"],
      benefits: "Reduz 55% o tempo de resolução"
    },
    {
      id: 10,
      name: "Auditorias Internas", 
      category: "Qualidade",
      icon: <CheckCircle className="w-6 h-6" />,
      description: "Planejamento e execução de auditorias com relatórios automáticos",
      features: ["Planejamento anual", "Checklists digitais", "Relatórios automáticos", "Acompanhamento"],
      benefits: "Aumenta 45% a eficiência das auditorias"
    },
    {
      id: 11,
      name: "Controle de Documentos",
      category: "Qualidade",
      icon: <Database className="w-6 h-6" />,
      description: "Gestão documental com controle de versões e distribuição",
      features: ["Controle de versão", "Distribuição automática", "Aprovação digital", "Trilha de auditoria"],
      benefits: "Garante 100% de conformidade documental"
    },
    {
      id: 12,
      name: "Ações Corretivas",
      category: "Qualidade", 
      icon: <Settings className="w-6 h-6" />,
      description: "Workflow para ações corretivas e preventivas com 8D",
      features: ["Metodologia 8D", "Cronograma automático", "Eficácia das ações", "Relatórios gerenciais"],
      benefits: "Melhora 60% a eficácia das correções"
    },

    // Categoria: TI Services
    {
      id: 13,
      name: "Chamados de TI",
      category: "TI Services",
      icon: <HeadphonesIcon className="w-6 h-6" />,
      description: "Service desk completo com SLA e escalação automática",
      features: ["Catálogo de serviços", "SLA automático", "Escalação inteligente", "Base conhecimento"],
      benefits: "Aumenta 70% a satisfação dos usuários"
    },
    {
      id: 14,
      name: "Gestão de Ativos",
      category: "TI Services",
      icon: <Package className="w-6 h-6" />,
      description: "Controle completo do ciclo de vida de ativos de TI",
      features: ["Inventário automático", "Controle de garantia", "Manutenções preventivas", "Relatórios financeiros"],
      benefits: "Reduz 35% os custos com ativos"
    },
    {
      id: 15,
      name: "Portal do Fornecedor",
      category: "TI Services",
      icon: <Truck className="w-6 h-6" />,
      description: "Portal self-service para fornecedores com integração completa",
      features: ["Cadastro digital", "Homologação automática", "Portal de pedidos", "Avaliação contínua"],
      benefits: "Acelera 80% o processo de compras"
    },
    {
      id: 16,
      name: "Segurança da Informação",
      category: "TI Services",
      icon: <Shield className="w-6 h-6" />,
      description: "Gestão de incidentes e políticas de segurança da informação",
      features: ["Gestão de incidentes", "Políticas digitais", "Treinamentos online", "Relatórios compliance"],
      benefits: "Aumenta 90% a conformidade de segurança"
    }
  ];

  const stats = [
    {
      number: "16",
      label: "FluigApps Disponíveis",
      description: "Soluções pré-construídas"
    },
    {
      number: "20+", 
      label: "Empresas Atendidas",
      description: "Clientes satisfeitos"
    },
    {
      number: "95%",
      label: "Taxa de Sucesso",
      description: "Projetos bem-sucedidos"
    },
    {
      number: "24h",
      label: "Tempo de Deploy",
      description: "Implementação rápida"
    }
  ];

  const getFilteredApps = (category: string) => {
    return fluigApps.filter(app => app.category === category);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Catálogo FluigApps - 16 Soluções Pré-construídas"
        description="16 FluigApps prontos para implementação em 24h: Financeiro, RH, Qualidade e TI. Integração TOTVS nativa, 95% taxa de sucesso e mais de 20 empresas atendidas."
        keywords={['fluigapps catálogo', 'soluções fluig prontas', 'apps fluig', 'implementação rápida fluig', 'workflows pré-construídos', 'automação processos fluig', 'catálogo soluções totvs']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-6xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 fs-gradient-text">
                Flows & FluigApps
              </h1>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8">
                Catálogo completo com 16 soluções pré-construídas para automação
                de processos empresariais com a plataforma Fluig.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="fs-primary"
                  size="lg"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Gostaria de conhecer os FluigApps da Future Station.', '_blank')}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Solicitar Demo
                </Button>
                <Button
                  variant="fs-secondary"
                  size="lg"
                  onClick={() => document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver Catálogo
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Estatísticas */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <Card key={index} className="bg-white/80 dark:bg-gradient-to-br dark:from-fs-purple-500/10 dark:to-fs-cyan-500/10 border-border text-center">
                  <CardContent className="p-6">
                    <div className="text-4xl font-bold fs-gradient-text mb-2">
                      {stat.number}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground dark:text-white mb-1">
                      {stat.label}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70 text-sm">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Categorias */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Categorias de Soluções
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Nossos FluigApps estão organizados em 4 categorias principais 
                para atender todos os departamentos da sua empresa
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {categories.map((category, index) => (
                <Card key={index} className={`bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-${category.color}-500/40 transition-all duration-300 text-center`}>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-foreground dark:text-white mb-2">
                      {category.name}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70">
                      {category.description}
                    </p>
                    <div className="mt-4">
                      <span className={`text-${category.color}-500 text-sm font-medium`}>
                        {getFilteredApps(category.name).length} FluigApps
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Catálogo de FluigApps */}
        <section id="catalog" className="py-24 bg-background dark:bg-fs-dark-950 relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-fs-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fs-magenta-500/10 rounded-full blur-3xl" />
          </div>

          <Container className="py-20 relative z-10">
            <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Catálogo de FluigApps
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                16 soluções pré-construídas prontas para implementação. 
                Cada FluigApp pode ser customizado conforme suas necessidades.
              </p>
            </div>

            {categories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-16">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-foreground dark:text-white mb-2 flex items-center">
                    <span className={`w-4 h-4 bg-${category.color}-500 rounded-full mr-3`}></span>
                    {category.name}
                  </h3>
                  <p className="text-muted-foreground dark:text-white/70">{category.description}</p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getFilteredApps(category.name).map((app, appIndex) => (
                    <Card key={appIndex} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300 group">
                      <CardHeader>
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="text-fs-purple-500 group-hover:text-fs-cyan-500 transition-colors">
                            {app.icon}
                          </div>
                          <CardTitle className="text-foreground dark:text-white text-lg">
                            {app.name}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-muted-foreground dark:text-white/70">
                          {app.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-foreground dark:text-white mb-2">Principais Recursos:</h4>
                            <ul className="text-sm text-muted-foreground dark:text-white/70 space-y-1">
                              {app.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-center">
                                  <CheckCircle className="w-3 h-3 text-fs-purple-500 mr-2 flex-shrink-0" />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="bg-fs-purple-500/10 rounded-lg p-3">
                            <h4 className="text-sm font-semibold text-fs-purple-500 mb-1">Benefício Principal:</h4>
                            <p className="text-sm text-muted-foreground dark:text-white/70">{app.benefits}</p>
                          </div>

                          <Button 
                            variant="fs-secondary" 
                            size="sm" 
                            className="w-full"
                            onClick={() => window.open(`https://wa.me/5581981658439?text=Olá! Gostaria de conhecer mais sobre o ${app.name}.`, '_blank')}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Solicitar Demo
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
          </Container>
        </section>

        {/* Integração TOTVS */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <Card className="bg-gradient-to-r from-fs-purple-500/10 via-fs-magenta-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
              <CardContent className="p-12 text-center">
                <Building className="w-16 h-16 text-fs-purple-500 mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                  Integração Nativa TOTVS
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70 mb-8 max-w-3xl mx-auto">
                  Todos os nossos FluigApps possuem integração nativa com o ecossistema TOTVS, 
                  garantindo sincronização perfeita com ERP Protheus, RM e outros sistemas.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold fs-gradient-text mb-2">100%</div>
                    <p className="text-muted-foreground dark:text-white/70">Compatibilidade TOTVS</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold fs-gradient-text mb-2">Zero</div>
                    <p className="text-muted-foreground dark:text-white/70">Retrabalho Manual</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold fs-gradient-text mb-2">Real-time</div>
                    <p className="text-muted-foreground dark:text-white/70">Sincronização</p>
                  </div>
                </div>
                <Button 
                  variant="fs-primary" 
                  size="lg"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Gostaria de saber mais sobre a integração TOTVS dos FluigApps.', '_blank')}
                >
                  Conhecer Integrações
                </Button>
              </CardContent>
            </Card>
          </div>
        </Container>

        {/* CTA Final */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
              Pronto para Implementar?
            </h2>
            <p className="text-lg text-muted-foreground dark:text-white/70 mb-8 max-w-2xl mx-auto">
              Nossos FluigApps podem ser implementados em até 24 horas. 
              Solicite uma demonstração personalizada e veja como podemos 
              transformar seus processos empresariais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="fs-primary" 
                size="lg"
                onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Gostaria de agendar uma demonstração dos FluigApps Future Station.', '_blank')}
              >
                Agendar Demonstração
              </Button>
              <Button 
                variant="fs-secondary" 
                size="lg"
                onClick={() => window.location.href = '/contato'}
              >
                Solicitar Proposta
              </Button>
            </div>
          </div>
        </Container>

        {/* Related Links */}
        <section className="py-20 bg-gradient-to-b from-fs-section to-fs-section/50">
          <div className="max-w-6xl mx-auto px-6">
            <RelatedLinks 
              title="Serviços para Implementar FluigApps"
              links={[
                {
                  title: "Serviços de TI Especializados",
                  description: "Outsourcing Fluig para implementar e manter seus FluigApps com suporte técnico 24/7.",
                  href: "/servicos/ti",
                  category: "Serviços"
                },
                {
                  title: "Automação de Processos",
                  description: "Transforme seus processos manuais usando nossos FluigApps como base para automação completa.",
                  href: "/automacao-processos",
                  category: "Automação"
                },
                {
                  title: "Consultoria Fluig Especializada",
                  description: "Planejamento estratégico para implementação dos FluigApps ideais para sua empresa.",
                  href: "/consultoria-fluig",
                  category: "Consultoria"
                },
                {
                  title: "Consultoria em IA",
                  description: "Combine FluigApps com inteligência artificial para processos ainda mais inteligentes.",
                  href: "/consultoria-ia",
                  category: "Consultoria"
                }
              ]}
            />
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Flows;